import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useBasePath } from '../../../../lib/config/nav-helpers';
import { DataCard, StatusBadge, ScoreRing, SkeletonList, QueryError } from '../../../../components/ui';
import { trustAlertRows, type TrustScoresResponse } from '../../../../lib/trust-scores';

export interface TrustAlertsWidgetProps {
  trustAlerts: { data?: TrustScoresResponse; isLoading: boolean; isError: boolean; error: unknown; refetch: () => void };
}

export function TrustAlertsWidget({ trustAlerts }: TrustAlertsWidgetProps) {
  const navigate = useNavigate();
  const basePath = useBasePath();
  const scored = trustAlerts.data?.trust_scores.length ?? 0;
  const alerts = trustAlerts.data ? trustAlertRows(trustAlerts.data) : [];
  const totalRisk = alerts.reduce((s, a) => s + (a.revenue_at_risk_cents ?? 0), 0);

  return (
    <DataCard
      title="Trust Alerts"
      subtitle="Contacts with declining relationship health"
      accent={alerts.length > 0 ? 'warn' : 'default'}
      badge={totalRisk > 0 ? <StatusBadge status="warning" label={`$${Math.round(totalRisk / 100_000)}k at risk`} size="xs" /> : undefined}
    >
      {trustAlerts.isLoading ? <SkeletonList rows={3} /> : trustAlerts.isError ? <QueryError error={trustAlerts.error} onRetry={() => trustAlerts.refetch()} compact /> : alerts.length === 0 ? (
        <p className="text-2xs text-ink-dim italic">
          {scored === 0
            ? 'No trust-score data yet — scores are written by the full intelligence run.'
            : `None of the ${scored} lowest-scored contacts is declining or critical.`}
        </p>
      ) : (
        <div className="space-y-1.5">
          {alerts.map((a) => (
            <div
              key={a.contact_email}
              onClick={() => navigate(`${basePath}/clients`)}
              className="flex items-center gap-3 rounded-lg border border-border px-3 py-2 cursor-pointer hover:bg-surface-1 hover:border-border-strong transition-colors"
            >
              <ScoreRing score={Math.round(a.trust_score)} size={32} strokeWidth={3} colorScale="health" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-medium text-ink">{a.contact_name || a.contact_email}</p>
                {a.recommended_action && <p className="truncate text-2xs text-ink-dim">{a.recommended_action}</p>}
              </div>
              <div className="text-right shrink-0">
                <p className={clsx('text-2xs font-semibold', a.trend === 'critical' ? 'text-danger' : 'text-warn')}>
                  {a.trend === 'critical' ? '⚠ Critical' : '↓ Declining'}
                </p>
                {(a.revenue_at_risk_cents ?? 0) > 0 && (
                  <p className="text-2xs font-mono text-ink-dim">${Math.round((a.revenue_at_risk_cents ?? 0) / 100).toLocaleString()}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </DataCard>
  );
}
